import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslate } from "helpers";
import { ReactSVG } from "react-svg";

import { Routes as SalesRoutes } from "ui/modules/sales/navigation/routes";
import { getListingAPI } from "services/api/sales/listings/api";
import { useApiCall } from "services/api/hooks/useApiCall";
import { getSalesChannelsAPI } from "services/api/sales/channels/api";

import { ListingDetailPageCmp } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Loader } from "ui/components/Loader";
import { Button } from "ui/components/Button/Button";
import ListingGalleryPreview from "./components/ListingGalleryPreview/ListingGalleryPreview";
import ListingSpecification from "./components/ListingSpecification/ListingSpecification";
import ListingEditCard from "ui/modules/sales/pages/listings/components/ListingEditCard/ListingEditCard";
import ChannelToggleModal, { ChannelToggleMode } from "./components/ChannelToggleModal/ChannelToggleModal";

import { SALES_LISTING_DETAIL_PAGE_TITLE, SALES_LISTING_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { CHANNEL_SORT_ORDER } from "services/api/sales/listings/consts";
import Badge from "ui/components/Badge/Badge";
import { getListingStatus } from "ui/modules/sales/pages/listings/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";

const ListingDetailPage: ListingDetailPageCmp = () => {
  const { id } = useParams();
  const { t } = useTranslate();

  const { data: listing, isLoading, refetch } = useApiCall(
    () => getListingAPI(id!),
    [id],
    { onNotFound: SalesRoutes.ListingSearchPageRouter }
  );

  // Kanali s neinstaliranom integracijom se ne smiju aktivirati ni pauzirati —
  // dostupnost stiže uz šifrarnik kanala (`isAvailable`).
  const { data: salesChannels } = useApiCall(() => getSalesChannelsAPI(), []);

  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  // Pause/activate sad uvijek prolazi kroz modal s odabirom kanala — bulk
  // /pause i /unpause endpointi se više ne pozivaju iz ovog ekrana.
  const [channelToggleMode, setChannelToggleMode] = useState<ChannelToggleMode | null>(null);

  if (!id) return <></>;
  if (isLoading || !listing) return <Loader isLoading={true}>{null}</Loader>;

  // Vehicle + slike stižu u all-in-one payloadu iz GET /sales/listings/{id}.
  const vehicle = listing.vehicle;
  const pictures = listing.fleetVehiclePictures;

  const sortedChannels = [...listing.channels].sort((a, b) => {
    const orderA = CHANNEL_SORT_ORDER[a.salesChannelCode ?? ""] ?? 99;
    const orderB = CHANNEL_SORT_ORDER[b.salesChannelCode ?? ""] ?? 99;
    return orderA - orderB;
  });

  const marketplaceChannel = sortedChannels.find(c => c.salesChannelCode === "marketplace");
  // U detalju više nema per-kanal taba — kanali se prikazuju kao popis u
  // sadržaju. Galerija/spec uzimaju marketplace kao baseline (fallback na
  // prvi sortirani ako marketplace ne postoji).
  const baselineChannel = marketplaceChannel ?? sortedChannels[0];
  const hasActiveChannel = sortedChannels.some(c => c.isActive);
  // Na detalju oglas uvijek postoji, pa je status ili aktivan ili pauziran.
  const listingStatus = getListingStatus(true, hasActiveChannel);
  // Kanali kojima se smije mijenjati stanje: oni čija je integracija instalirana
  // (vlastiti kanali nemaju integraciju pa su uvijek tu). Dok se šifrarnik ne
  // učita, radi se sa svima — tada se ionako ne može ništa kliknuti.
  const unavailableSalesChannelIds = new Set(
    (salesChannels ?? []).filter(c => c.isAvailable === false).map(c => c.id)
  );
  const manageableChannels = sortedChannels.filter(
    c => !unavailableSalesChannelIds.has(c.salesChannelId)
  );

  // Marketplace se pauzira i aktivira kao svaki drugi kanal — oglas se smije
  // ugasiti u cijelosti, pa se gumbi vežu samo na to ima li što (de)aktivirati.
  const hasPausableChannel = manageableChannels.some(c => c.isActive);
  const hasInactiveChannel = manageableChannels.some(c => !c.isActive);

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SALES_LISTING_SEARCH_PAGE_TITLE.text, route: SALES_LISTING_SEARCH_PAGE_TITLE.route },
  ];

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div className="flex gap-3 items-start">
                {vehicle?.defaultImage && (
                  <img
                    src={vehicle.defaultImage}
                    alt={listing.fleetVehicleName ?? ""}
                    title={listing.fleetVehicleName ?? ""}
                    className="h-12 w-12 object-contain rounded-full"
                  />
                )}
                <div>
                  <div className="flex gap-2 items-center">
                    <PageTitle
                      title={`${t(SALES_LISTING_DETAIL_PAGE_TITLE.text)} [${listing.fleetVehicleName ?? ""}]`}
                      icon={SALES_LISTING_DETAIL_PAGE_TITLE.icon}
                      iconColor={SALES_LISTING_DETAIL_PAGE_TITLE.iconColor}
                    />
                    {/* Read-only status pill — isti stil kao VehicleStatusBadge ali bez chevrona/klika. */}
                    <Badge
                      text={listingStatus.label}
                      variant={listingStatus.variant}
                      height="small"
                      showDot
                    />
                  </div>
                  <Breadcrumb items={breadcrumbs} />
                </div>
              </div>
              <div className="flex gap-2 items-center">

                {/* Oba gumba su vidljiva kad god je primjenjivo — user može
                    aktivirati nove kanale i kad već postoji bar jedan
                    aktivan, i obrnuto pauzirati neke iako još ima
                    neaktivnih. */}
                {hasInactiveChannel && (
                  <Button
                    variant="success"
                    startIcon="/media/icons/play.svg"
                    onClick={() => setChannelToggleMode("activate")}
                  >
                    Aktiviraj oglas
                  </Button>
                )}
                {hasPausableChannel && (
                  <Button
                    variant="warning"
                    startIcon="/media/icons/pause.svg"
                    onClick={() => setChannelToggleMode("pause")}
                  >
                    Pauziraj oglas
                  </Button>
                )}
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  onClick={() => setShowEditModal(true)}
                >
                  Uredi oglas
                </Button>
              </div>
            </div>
          </PageHeader>

          {/* Single static tab — više nema per-kanal navigacije. */}
          <div className="flex">
            <div className="group flex items-center gap-2 font-semibold pl-2 pr-4 py-2 ml-3 whitespace-nowrap border-b-2 text-sky-600 border-sky-600">
              <ReactSVG src="/media/icons/info.svg" className="w-4 h-4 [&_svg]:w-4 [&_svg]:h-4 [&_path]:stroke-sky-600" />
              Detalji oglasa
            </div>
          </div>
        </div>

        <div className="px-0 pt-2 flex justify-center">
          {baselineChannel && (
            <div className="w-1/2 flex flex-col gap-4">
              <div className="bg-white rounded-sm border ">
                <ListingGalleryPreview
                  allPictures={pictures}
                  channel={baselineChannel}
                  marketplaceChannel={marketplaceChannel}
                />
                <ListingSpecification
                  listing={listing}
                  channel={baselineChannel}
                  vehicle={vehicle}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <ListingEditCard
        show={showEditModal}
        fleetVehicleId={listing.fleetVehicleId ?? null}
        fleetVehicleName={listing.fleetVehicleName ?? null}
        listingId={listing.id}
        onSuccess={() => {
          setShowEditModal(false);
          refetch();
        }}
        onClose={() => setShowEditModal(false)}
      />
      {channelToggleMode && (
        <ChannelToggleModal
          show={!!channelToggleMode}
          mode={channelToggleMode}
          listing={listing}
          channelIds={manageableChannels.map(c => c.id)}
          onClose={() => setChannelToggleMode(null)}
          onSuccess={() => {
            setChannelToggleMode(null);
            refetch();
          }}
        />
      )}
    </>
  );
};

ListingDetailPage.route = SalesRoutes.ListingDetailPageRouter;

export default ListingDetailPage;
